import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { useVisitationSchedule } from "../../contexts/VisitationScheduleContext";

import DeleteVisitationScheduleButton from "../../components/DeleteVisitationSchedule";
import VisitationScheduleStatusBadge from "../../components/VisitationScheduleStatusBadge";
import VisitationScheduleButton from "../../components/VisitationScheduleButton";
import VisitationScheduleLiveButton from "./components/VisitationScheduleLiveButton";
import { useParticipant } from "../../contexts/ParticipantContext";
import { generateCalendarEvents } from "../../helpers/generateCalendarEvents";
import { CustodyTimeline } from "../../components/CustodyTimeline";

const VisitationScheduleDetailPage = () => {
  const { visitationScheduleId } = useParams<{
    visitationScheduleId: string;
  }>();
  const navigate = useNavigate();
  const { participants } = useParticipant();
  const {
    loading,
    error,
    selectedVisitationSchedule,
    setSelectedVisitationSchedule,
  } = useVisitationSchedule();

  useEffect(() => {
    if (visitationScheduleId) {
      setSelectedVisitationSchedule(visitationScheduleId);
    }
  }, [visitationScheduleId, participants]);

  if (loading.details)
    return <LoadingView title="Loading Visitation Schedule" />;
  if (error.details) return <ErrorView error={error.details} />;
  if (!selectedVisitationSchedule)
    return <ErrorView error={new Error("Visitation Schedule not found")} />;

  const { name, status, updatedAt, type, interval, startDate, endDate } =
    selectedVisitationSchedule;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Heading>{name}</Heading>
            <VisitationScheduleStatusBadge status={status} />
          </div>
          {updatedAt && (
            <Text className="text-sm text-gray-500">
              Last updated {moment(updatedAt).format("MMM D, YYYY")}
            </Text>
          )}
        </div>
        <div className="flex gap-2">
          <VisitationScheduleLiveButton
            visitationSchedule={selectedVisitationSchedule}
          />
          <VisitationScheduleButton schedule={selectedVisitationSchedule} />
          <DeleteVisitationScheduleButton
            visitationScheduleId={selectedVisitationSchedule._id}
            onDelete={() => navigate("/visitation-schedule")}
          />
        </div>
      </div>

      {/* Meta Info */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Subheading>Schedule Details</Subheading>
          <ul className="space-y-1 text-sm text-gray-700">
            <li>
              <strong>Type:</strong> {type}
            </li>
            <li>
              <strong>Interval:</strong> {interval}
            </li>
            <li>
              <strong>Start Date:</strong>{" "}
              {moment(startDate).format("MMM D, YYYY")}
            </li>
            <li>
              <strong>End Date:</strong>{" "}
              {endDate ? moment(endDate).format("MMM D, YYYY") : "Ongoing"}
            </li>
          </ul>
        </div>
      </div>

      <Subheading>Custody Timeline</Subheading>
      <div className="overflow-hidden bg-white rounded shadow">
        <CustodyTimeline
          rotation={selectedVisitationSchedule.rotation}
          startDate={selectedVisitationSchedule.startDate}
          participants={participants}
        />
      </div>
    </div>
  );
};

export default VisitationScheduleDetailPage;
